import { getPlanTerms, type BillingPlanV2Tier, type PricingEdition } from '@highlight/common'
import { v2TierFor } from '@renderer/chat/utils/billingUtils'
import type { PlanBannerCopy } from './plan-banner-rules'

// Pure module, no atoms or React, so the announcement rules stay testable.

/**
 * The day existing 2.0 subscriptions move onto the revamp lineup (FUN-86).
 * The banner names it and retires the day after.
 */
export const PLAN_CHANGE_DATE = new Date(2026, 9, 23)

const DAY_MS = 24 * 60 * 60 * 1000

export interface PlanChangeAnnouncement {
  /** The 2.0 tier the account holds, trial or paid. */
  tier: BillingPlanV2Tier
  /** The tier the account lands on, which colours the banner. Lite folds into Pro. */
  ctaPlan: BillingPlanV2Tier
  /** One occurrence per tier and date, so a rescheduled flip announces itself again. */
  dismissKey: string
}

export interface PlanChangeInputs {
  /** The raw billed plan (SubscriptionInfo.plan), the identity the move is keyed on. */
  plan: string | undefined
  edition: PricingEdition
  /** The announcement's own flag, so the heads-up starts when the emails do. */
  announced: boolean
  now?: number
}

/**
 * Whether the account is due to move on the flip and may hear about it: the
 * announcement switched on, a 2.0 tier or trial, still on the pre-revamp
 * lineup, before the announced day has passed. Legacy Pro and Free are not
 * moved by the flip, so they get nothing.
 */
export function resolvePlanChangeAnnouncement(inputs: PlanChangeInputs): PlanChangeAnnouncement | null {
  const { plan, edition, announced, now = Date.now() } = inputs
  if (!announced) return null
  if (edition === 'v2-revamp') return null
  if (now >= PLAN_CHANGE_DATE.getTime() + DAY_MS) return null
  const tier = v2TierFor(plan)
  if (!tier) return null
  return {
    tier,
    ctaPlan: tier === 'lite' ? 'pro2' : tier,
    dismissKey: `plan-change:${tier}:${PLAN_CHANGE_DATE.toISOString().slice(0, 10)}`,
  }
}

const formatCount = (n: number) => n.toLocaleString('en-US')

/** "Oct 23" */
const formatChangeDate = (date: Date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })

function transcription(hours: number | null): string {
  return hours === null ? 'unlimited transcription' : `${formatCount(hours)} hours of transcription`
}

/** Rounded down, so the banner never overstates the move. */
function transcriptionMultiple(from: number | null, to: number | null): string {
  if (to === null) return 'unlimited transcription'
  if (from === null || from <= 0) return transcription(to)
  return `${Math.floor(to / from)}x the transcription`
}

/**
 * What the announcement says. Every figure comes from the revamp's plan terms,
 * which match the Orb versions the flip moves subscriptions onto.
 */
export function planChangeAnnouncementCopy(announcement: PlanChangeAnnouncement): PlanBannerCopy {
  const date = formatChangeDate(PLAN_CHANGE_DATE)
  const next = getPlanTerms(announcement.ctaPlan, 'v2-revamp')
  switch (announcement.tier) {
    case 'pro2':
      return {
        title: `Pro is getting a major upgrade on ${date}`,
        body: `$${next.monthlyPriceUsd} a month, ${transcription(next.monthlyMeetingHours)}, ${formatCount(next.monthlyCredits)} credits, and custom skills.`,
        cta: 'See what’s new',
      }
    case 'lite': {
      const current = getPlanTerms('lite', 'v2')
      const credits = Math.floor(next.monthlyCredits / current.monthlyCredits)
      return {
        title: `You’re getting upgraded to Pro on ${date}`,
        body: `Your same price, with ${transcriptionMultiple(current.monthlyMeetingHours, next.monthlyMeetingHours)}, ${credits}x the credits, and skills.`,
        cta: 'See what’s new',
      }
    }
    case 'max':
      return {
        title: `Max is getting bigger on ${date}`,
        body: `${formatCount(next.monthlyCredits)} credits a month and ${transcription(next.monthlyMeetingHours)}.`,
        cta: 'See what’s new',
      }
  }
}
